import { supportedCurrencies } from "@circle-react/config/Currencies";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { ChangeEvent } from "react";
import { useMemo } from "react";

interface CurrencySelectorProps {
  billingCurrency: string | null;
  className?: string;
  onCurrencyChange?: (currency: string) => void;
  useCurrencySymbol?: boolean;
}

export const CurrencySelector = ({
  billingCurrency,
  onCurrencyChange = () => {},
  useCurrencySymbol = false,
  className,
}: CurrencySelectorProps) => {
  const value = billingCurrency?.toUpperCase() ?? "";

  const options = useMemo(
    () =>
      Object.entries(supportedCurrencies).map(
        ([key, { code, flag, symbol }]) => (
          <option key={key} value={code}>
            {useCurrencySymbol ? `${symbol} ${code}` : `${flag} ${code}`}
          </option>
        ),
      ),
    [useCurrencySymbol],
  );

  if (!billingCurrency) return null;

  const selectClasses = useCurrencySymbol
    ? classNames(
        "appearance-none border-none bg-transparent text-xs text-slate-700 focus:outline-none dark:text-slate-300",
        className,
      )
    : classNames(
        "border-primary block appearance-none rounded-md border bg-white py-2 pl-3 pr-8 text-md leading-6",
        "transition duration-150 ease-in-out focus:border-blue-300 focus:outline-none",
        className,
      );

  return (
    <div className="flex items-center">
      <select
        className={selectClasses}
        value={value}
        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
          onCurrencyChange(e.target.value)
        }
      >
        {options}
      </select>
    </div>
  );
};
